import React, { useRef, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Place, Image, Videocam, Description } from "@mui/icons-material";
import whatsappBg from '../../../../assets/images/backgrounds/fondodoodle2.png';

export default function PhonePreview({
  name,
  headerType,
  headerText,
  headerTextVariable,
  headerMedia,
  body,
  footer,
  quickReplies = [],
  ctas = [],
  category,
  addSecurityRecommendation,
  addExpirationTime,
  codeExpirationMinutes,
  templateType,
  cards = [],
}) {
    // console.log("templateType",templateType)
    // console.log("cards",cards)
  const headerTextResolved =
    headerType === "text"
      ? headerTextVariable
        ? headerText.replace("{{1}}", headerTextVariable)
        : headerText
      : "";

  // 🧩 Bloque especial para Carousel
  const carouselRef = useRef(null);
  const isHoveringRef = useRef(false);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container || cards.length <= 1) return;

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (isHoveringRef.current) return;
      currentIndex = (currentIndex + 1) % cards.length;
      const child = container.children[currentIndex];
      if (child) {
        child.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      }
    }, 3500);

    return () => clearInterval(interval);
  }, [cards.length]);

  if (
    category?.toUpperCase() === "MARKETING" &&
    templateType?.toUpperCase() === "CAROUSEL" &&
    cards.length > 0
  ) {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          p: 2,
          bgcolor: (t) =>
            t.palette.mode === "light" ? "grey.50" : "background.paper",
          backgroundImage: `url(${whatsappBg})`,
        }}
        aria-label="preview"
      >
        <Stack gap={2}>
          <Typography variant="subtitle2" color="text.primary">
            Preview
          </Typography>

          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: "white" }}>
            <Stack gap={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  {name || "Template name"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  00:00 AM
                </Typography>
              </Box>

              {/* Body general del carrusel (arriba de las cards) */}
              {body && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {body}
                </Typography>
              )}

              {/* Cards del carrusel */}
              <Stack
                ref={carouselRef}
                direction="row"
                gap={2}
                onMouseEnter={() => { isHoveringRef.current = true; }}
                onMouseLeave={() => { isHoveringRef.current = false; }}
                sx={{
                  overflowX: "auto",
                  pb: 1,
                  scrollSnapType: "x mandatory",
                  "&::-webkit-scrollbar": { height: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    background: "divider",
                    borderRadius: 3,
                  },
                }}
              >
                {cards.map((card, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      minWidth: 220,
                      maxWidth: 220,
                      flexShrink: 0,
                      bgcolor: "white",
                      overflow: "hidden",
                      borderColor: "divider",
                      scrollSnapAlign: "start",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Stack gap={1}>
                      {/* Imagen o video */}
                      {["IMAGE", "VIDEO"].includes(
                        card.headerType?.toUpperCase()
                      ) && (
                        <Box sx={{ height: 140, overflow: "hidden" }}>
                          {card.headerType?.toUpperCase() === "IMAGE" && (card.mediaUrl ? (
                            <img
                              src={card.mediaUrl}
                              alt="carousel media"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", bgcolor: "grey.100" }}>
                              <Image sx={{ fontSize: 48, color: "grey.400" }} />
                            </Box>
                          ))}
                          {card.headerType?.toUpperCase() === "VIDEO" && (card.mediaUrl ? (
                            <video
                              src={card.mediaUrl}
                              controls
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", bgcolor: "grey.100" }}>
                              <Videocam sx={{ fontSize: 48, color: "grey.400" }} />
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Descripción */}
                      <Typography
                        variant="body2"
                        sx={{
                          px: 1.5,
                          pt: 0.5,
                          minHeight: 60,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {card.body || "Description"}
                      </Typography>

                      {/* Botones */}
                      {card.buttons?.length > 0 && (
                        <Stack gap={0.5} sx={{ px: 1.5, pb: 1 }}>
                          {card.buttons.map((btn, i) => (
                            <Button
                              key={i}
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{
                                textTransform: "none",
                                fontSize: 12,
                                justifyContent: "center",
                              }}
                            >
                              {btn.text}
                            </Button>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    );
  }

  // 🧱 Default preview (text, image, video, etc.)
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 4,
        p: 2,
        bgcolor: (t) =>
          t.palette.mode === "light" ? "#f5efe9" : "background.paper",
        backgroundImage: `url(${whatsappBg})`,
      }}
      aria-label="preview"
    >
      <Stack gap={2}>
        <Typography variant="subtitle2" color="text.primary">
          Preview
        </Typography>

        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: "white" }}>
          <Stack gap={1}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption" color="text.secondary">
                {name || "Template name"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                00:00 AM
              </Typography>
            </Box>

            {/* Header */}
            {headerType === "text" &&
              headerTextResolved &&
              category !== "authentication" && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {headerTextResolved}
                </Typography>
              )}

            {/* Media */}
            {["image", "video", "document"].includes(headerType) &&
              category !== "authentication" && (
                <Box
                  mt={1}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    height: 220,
                    bgcolor: headerMedia ? "transparent" : "grey.100",
                  }}
                >
                  {headerType === "image" && (headerMedia ? (
                    <img
                      src={headerMedia}
                      alt="Preview"
                      style={{ objectFit: "cover", width: "100%", height: "100%" }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Image sx={{ fontSize: 64, color: "grey.400" }} />
                    </Box>
                  ))}
                  {headerType === "video" && (headerMedia ? (
                    <video
                      src={headerMedia}
                      controls
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Videocam sx={{ fontSize: 64, color: "grey.400" }} />
                    </Box>
                  ))}
                  {headerType === "document" && (headerMedia ? (
                    <iframe
                      src={headerMedia}
                      title="PDF Preview"
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Description sx={{ fontSize: 64, color: "grey.400" }} />
                    </Box>
                  ))}
                </Box>
              )}

            {/* Location */}
            {headerType === "location" && category !== "authentication" && (
              <Box mt={1} sx={{ display: "flex", justifyContent: "center" }}>
                <Place fontSize="large" color="disabled" />
              </Box>
            )}

            {/* Body */}
            {category !== "authentication" && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {body || "Cuerpo del mensaje..."}
              </Typography>
            )}

            {/* Authentication special view */}
            {category === "authentication" && (
              <>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  123456 is your verification code.
                </Typography>
                {addSecurityRecommendation && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    For your security, do not share this code.
                  </Typography>
                )}
                {addExpirationTime && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    This code expires in {codeExpirationMinutes || 0} minutes
                  </Typography>
                )}
              </>
            )}

            {/* Footer */}
            {footer && category !== "authentication" && (
              <Typography variant="caption" color="text.secondary">
                {footer}
              </Typography>
            )}

            {/* Quick replies & CTAs */}
            {(quickReplies.length > 0 || ctas.length > 0) &&
              category !== "authentication" && (
                <Stack gap={1} sx={{ pt: 1 }}>
                  {quickReplies.filter((q) => q.trim() !== "").length > 0 && (
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {quickReplies
                        .filter((q) => q.trim() !== "")
                        .map((q, i) => (
                          <Button
                            key={`qr-${i}`}
                            size="small"
                            fullWidth
                            variant="contained"
                          >
                            {q}
                          </Button>
                        ))}
                    </Stack>
                  )}

                  {ctas.length > 0 && (
                    <Stack gap={1}>
                      {ctas.map((c, i) => (
                        <Button
                          key={`cta-${i}`}
                          size="small"
                          fullWidth
                          variant="contained"
                        >
                          {c.title || (c.type === "phone" ? "Call" : "Open link")}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
